<template>
  <div>
    <group-detail
      :items="members"
      :loading="loading"
      :breadcrumb="breadcrumb"
      :search="search"
      :count="memberCount"
      :page-size="pageSize"
      :group-name="group.name || ''"
      :group-info="group"
      @reached-bottom="onBottomReached"
      @tab-changed="onTabChange"
      @cell-clicked="goToClient"
      @add-clients="onAddClients"
      @delete="onDelete"
      @on-search="memberSearch"
      @group-name-change="onGroupNameChange"
    />
    <add-clients
      ref="addClients"
      :items="annotatedVals"
      :loading="nonMembersLoading"
      :group-name="group.name"
      :num-members="memberCount"
      :count="nonMemberCount"
      @add-to-group="addToGroup"
      @on-search="onSearch"
      @reached-bottom="addClientsBottom"
      @modal-closed="onModalClose"
    />
  </div>
</template>

<script>

import _ from 'lodash';
import { EventBus } from '@/event-bus';
import GroupDetail from './GroupDetail';
import AddClients from './GroupsList/AddClients';

export default {
  name: 'GroupDetailContainer',
  components: {
    GroupDetail,
    AddClients,
  },
  props: {
    source: {
      type: String,
      default: 'groups',
    },
  },
  data() {
    return {
      clientsSearch: '',
      clientsNextPage: true,
      clientsPage: 1,
      page: 1,
      search: '',
      pageSize: 50,
      membersNextPage: true,
      annotatedVals: [],
    };
  },
  computed: {
    loading() {
      return this.$store.state.groups.isLoading;
    },
    groupName() {
      if (!this.group) {
        return '';
      }
      return this.group.name;
    },
    group() {
      if (!this.$store.state.groups.group) {
        return {};
      }
      return this.$store.state.groups.group;
    },
    /** All customers that do not belong to this group.
     * */
    nonMembers() {
      if (this.loading) return [];
      return this.$store.state.customers.items.filter((i) => !this.inGroup(i.email));
    },
    nonMemberCount() {
      return this.$store.state.customers.count;
    },
    customers() {
      return this.$store.state.customers.items;
    },
    members() {
      return this.$store.state.groups.groupMembers;
    },
    memberIds() {
      return new Set(this.members.map((member) => member.pk));
    },
    annotatedCustomers() {
      const tempCustomers = this.$store.state.customers.items;
      return tempCustomers.map((customer) => ({
        ...customer,
        inGroup: this.memberIds.has(customer.pk),
      }));
    },
    nonMembersLoading() {
      return this.$store.state.customers.isLoading;
    },
    memberCount() {
      return this.$store.state.groups.membersCount;
    },
    isAdminMode() {
      return this.$store.state.account.isAdminMode;
    },
    breadcrumb() {
      if (this.isAdminMode) {
        return this.adminBreadCrumb();
      }
      return [
        {
          name: 'All Groups',
          link: '/clients/groups',
        },
        this.group.name
          ? {
            name: `${this.group.name}`,
          }
          : {
            name: '',
          },
      ];
    },
    profile() {
      if (!this.$store.state.profiles.item) {
        return {
          user: {
            first_name: '',
            last_name: '',
          },
        };
      }
      return this.$store.state.profiles.item;
    },
  },
  watch: {
    '$route.params.id': {
      handler() {
        const params = {
          scope: this.$store.getters['account/scope'],
        };
        const { id } = this.$route.params;
        this.$store.dispatch('groups/fetchGroup', { id, params }).then(() => {
          this.$store.dispatch('groups/reset').then(() => {
            this.fetchMembers();
          });
        });
      },
      immediate: true,
    },
    page(newval) {
      if (newval !== 1) {
        this.fetchMembers();
      }
    },
    clientsPage() {
      if (this.clientsNextPage) {
        this.fetchNonMembers();
      }
    },
    clientsSearch() {
      this.resetNonMembers();
      this.clientsPage = 1;
      this.fetchNonMembers();
    },
    search() {
      this.resetMembers();
      this.page = 1;
      this.fetchMembers();
    },
    annotatedCustomers(newVal) {
      this.annotatedVals = newVal;
    },
  },
  mounted() {
    if (this.isAdminMode) {
      this.$store.dispatch('profiles/fetchItem', this.$route.params.profile_id);
    }
  },
  methods: {
    adminBreadCrumb() {
      return [
        {
          name: 'Dashboard',
          link: '/dashboard',
        },
        {
          name: `${this.profile.user.first_name} ${this.profile.user.last_name}`,
          link: `/user/${this.profile.pk}`,
        },
        {
          name: 'Manage Groups',
          link: `/user/${this.profile.pk}/manage-groups`,
        },
        {
          name: `${this.group.name}`,
        },
      ];
    },
    // eslint-disable-next-line func-names
    memberSearch: _.debounce(function (evt) {
      this.search = evt;
    }, 200),
    // eslint-disable-next-line func-names
    onSearch: _.debounce(function (evt) {
      this.clientsSearch = evt;
    }, 200),
    inGroup(email) {
      if (this.members) {
        return this.members.map((m) => m.email).includes(email);
      }
      return false;
    },
    onTabChange(evt) {
      if (this.isAdminMode) {
        this.$router.push(`/admin/user/${this.profile.pk}/manage-clients`);
      } else if (evt === 1) {
        this.$router.push('/clients');
      } else if (evt === 2) {
        this.$router.push('/clients/groups');
      } else {
        this.$router.push('/clients/my');
      }
    },

    goToClient(evt) {
      this.$router.push(`/clients/${evt.pk}`);
    },
    fetchNonMembers() {
      if (this.nonMembersLoading) {
        console.debug('already loading');
        return;
      }
      const args = {
        page: this.clientsPage,
        page_size: this.pageSize,
        search: this.clientsSearch,
        include_groups: true,
      };
      this.$store.dispatch('customers/addPage', args).then((response) => {
        if (response && response.data) {
          this.clientsNextPage = response.data.next != null;
        }
      });
    },
    onAddClients() {
      this.$store.dispatch('customers/customReset');
      if (this.clientsPage !== 1) {
        this.clientsPage = 1;
        this.clientsNextPage = true;
        this.clientsSearch = '';
      } else {
        this.fetchNonMembers();
      }
      this.$refs.addClients.open();
    },
    addToGroup(selected) {
      const payload = {
        id: this.$route.params.id,
        ...selected,
      };
      this.$store.dispatch('groups/editGroup', payload).then(() => {
        this.resetMembers();
        this.fetchMembers();
      }).catch(() => {
        EventBus.$emit('toast-error', 'Couldn\'t add customers to group, please, try again later');
      });
      this.onModalClose();
    },
    onDelete(evt) {
      const payload = {
        id: this.$route.params.id,
        customer: evt.pk,
      };
      this.$store.dispatch('groups/deleteGroupMember', payload).then(() => {
        this.resetMembers();
        this.fetchMembers();
        this.fetchNonMembers();
      }).catch((e) => {
        EventBus.$emit('toast-error', 'Couldn\'t delete member, please try again later');
        console.error('Issue: ', e);
      });
    },
    fetchMembers() {
      const kwargs = {
        page: this.page,
        search: this.search,
        page_size: this.pageSize,
        include_groups: true,
      };
      this.$store.dispatch('groups/addGroupMembers', {
        id: this.$route.params.id,
        args: kwargs,
      }).then((response) => {
        if (response && response.data) {
          this.membersNextPage = response.data.next != null;
        }
      });
    },
    onPageChange(page) {
      this.page = page;
      window.scroll({ top: 0, left: 0 });
    },
    onGroupNameChange(newName) {
      const payload = {
        pk: this.$route.params.id,
        name: newName,
      };
      this.$store.dispatch('groups/patchGroup', payload);
    },
    addClientsBottom() {
      if (!this.$store.state.customers.isLoading) {
        this.clientsPage += 1;
      }
    },
    onModalClose() {
      this.clientsSearch = '';
      this.$store.dispatch('customers/reset');
    },
    onBottomReached() {
      if (this.membersNextPage) {
        this.page += 1;
      }
    },
    resetMembers() {
      this.$store.dispatch('groups/customReset');
    },
    resetNonMembers() {
      this.$store.dispatch('customers/customReset');
    },
  },
};
</script>
